import { Info } from 'react-feather';
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import FolderTree from './folder-tree';
import {
  getKbGDriveFoldersById,
  getKbAwsFoldersById,
  UploadDriveFolders,
  UploadAwsS3Folders,
} from '../services/kb';
import { ActionModal, MakeToast } from '../../../../components';
import { AwsSelected, FolderSelectionModalProps, TreeNode } from '../kb.props';
import { chunkInput, folderSelectionModalConfig } from '../data/constants';
import StyledTooltip from '../../../../components/tooltip';

function FolderHierarchy({
  open,
  service,
  dispatch,
  folderId,
  handleUploadCancelClick,
  onSyncSuccess,
  onSyncStarted,
  onSyncFailed,
}: FolderSelectionModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [selectedFoldersList, setSelectedFoldersList] = useState<string[] | AwsSelected[]>([]);
  const [componentState, setComponentState] = useState({
    chunk_words: 500,
    chunk_overlap: 250,
  });
  // 1. ADD NEW STATE to track if a new folder has been selected.
  const [hasNewSelection, setHasNewSelection] = useState<boolean>(false);
  const [folderTreeData, setFolderTreeData] = useState<TreeNode[]>([]);
  const [alreadySelectedList, setAlreadySelectedList] = useState<string[]>([]);

  // 2. USE EFFECT to determine if there are new selections.
  useEffect(() => {
    if (alreadySelectedList.length === 0 && selectedFoldersList.length > 0) {
      setHasNewSelection(true);
      return;
    }

    const alreadyUploadedSet = new Set(alreadySelectedList);
    let newSelectionFound = false;

    if (service === 'G-drive') {
      newSelectionFound = (selectedFoldersList as string[]).some(
        (id) => !alreadyUploadedSet.has(id),
      );
    } else if (service === 'aws') {
      // For AWS, we must construct the identifier string to match `alreadySelectedList`
      newSelectionFound = (selectedFoldersList as AwsSelected[]).some(
        (item) => !alreadyUploadedSet.has(`${item.bucket_name}`),
      );
    }

    if (newSelectionFound && !hasNewSelection) {
      setComponentState({
        chunk_words: 500,
        chunk_overlap: 250,
      });
    }

    setHasNewSelection(newSelectionFound);
  }, [selectedFoldersList, alreadySelectedList, service]);

  const handleSelectionChange = (selectedFolders: string[] | AwsSelected[]) => {
    setSelectedFoldersList(selectedFolders);
  };

  const handleCancelClick = () => {
    dispatch({ type: 'FOLDER_STRUCTURE_MODAL_OPEN', payload: false });
    dispatch({ type: 'SET_SERVICE', payload: 'system' });
    dispatch({ type: 'FOLDER_ID', payload: 0 });
    const params = new URLSearchParams(location.search);
    params.delete('uploadFolder');
    params.delete('folderId');
    params.delete('service');
    params.delete('status');
    navigate(`/knowledge-base`, { replace: true }); // clean up URL params
    dispatch({ type: 'SET_LOADING', payload: false });
    setComponentState({ chunk_words: 500, chunk_overlap: 250 });
    setSelectedFoldersList([]);
    setHasNewSelection(false);
    if (handleUploadCancelClick) {
      handleUploadCancelClick();
    }
  };

  const handleSync = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // 5. REFINE handleSync to check for new selections first.
    if (!hasNewSelection) {
      MakeToast('error', 'Please select at least one new folder to sync.');
      return;
    }

    if (selectedFoldersList.length === 0) {
      MakeToast('error', 'Please select at least one folder.');
      return;
    }

    if (componentState.chunk_words <= 0) {
      MakeToast('error', 'Chunk size must be greater than 0.');
      return;
    }

    if (componentState.chunk_overlap < 0) {
      MakeToast('error', 'Chunk overlap must be zero or positive.');
      return;
    }

    if (componentState.chunk_overlap >= componentState.chunk_words) {
      MakeToast('error', 'Chunk overlap must be smaller than chunk size.');
      return;
    }

    onSyncStarted?.(folderId);
    setIsDisabled(true);

    try {
      if (service === 'G-drive') {
        const Payload = {
          folderIds: selectedFoldersList as string[],
          chunkSize: componentState.chunk_words === 0 ? 500 : componentState.chunk_words,
          chunkOverlap: componentState.chunk_overlap === 0 ? 250 : componentState.chunk_overlap,
        };
        const response = await UploadDriveFolders(folderId, Payload);
        MakeToast('success', response?.data?.message || 'Files uploaded successfully');
        onSyncSuccess?.();
        handleCancelClick();
      } else if (service === 'aws') {
        const Payload = {
          buckets: selectedFoldersList as AwsSelected[],
          chunkSize: componentState.chunk_words,
          chunkOverlap: componentState.chunk_overlap,
        };
        const response = await UploadAwsS3Folders(folderId, Payload);
        MakeToast('success', response?.data?.message || 'Files uploaded successfully');
        onSyncSuccess?.();
        handleCancelClick();
      } else {
        MakeToast('error', 'Unsupported service.');
        onSyncFailed?.(folderId);
        // return;
      }
    } catch (error) {
      setSelectedFoldersList([]);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const msg = err?.response?.data?.message || err?.message || 'An error Occurred.';
      MakeToast('error', msg);
      onSyncFailed?.(folderId);
    } finally {
      setIsDisabled(false);
    }
  };

  const modalConfig = folderSelectionModalConfig?.find((cfg) => cfg.id === service);

  const hasFolderOrSubfolderSelected = selectedFoldersList.length > 0;

  useEffect(() => {
    const fetchFolders = async () => {
      try {
        setIsLoading(true);
        let response;
        if (service === 'G-drive') {
          response = await getKbGDriveFoldersById(folderId);
        } else if (service === 'aws') {
          response = await getKbAwsFoldersById(folderId);
        } else {
          throw new Error(`Unsupported service: ${service}`);
        }
        setFolderTreeData(response?.data?.data?.folders || []);
        setAlreadySelectedList(response?.data?.data?.already_uploaded_folders || []);
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const msg = err?.response?.data?.message || err?.message || 'Failed to fetch folders.';
        MakeToast('error', msg);
      } finally {
        setIsLoading(false);
      }
    };
    if (folderId && service) {
      fetchFolders();
    }
  }, [folderId, service]);

  const onChangeHandler = (key: string, value: number) => {
    setComponentState((prev) => ({ ...prev, [key]: value }));
  };

  const note: React.ReactNode = (
    <span className="text-sm text-yellow-700">
      <strong>Note:</strong> Only files in <code>.pdf</code>, <code>.txt</code> or{' '}
      <code>.docx</code> format are supported for upload at this time. Please ensure your folder
      contains <code>.pdf</code>, <code>.txt</code> or <code>.docx</code> files in order to upload
      it{' '}
    </span>
  );

  return (
    <ActionModal
      className="w-[550px]"
      open={open}
      modalTitle={<h3 className="text-base font-bold">{modalConfig?.Title || 'pp'}</h3>}
      modalDesc={modalConfig?.description}
      modalLogo={modalConfig?.modalLogo}
      resetData={handleCancelClick}
      confirmButtonText={isDisabled ? modalConfig?.savingButtonText : modalConfig?.saveButtonText}
      cancelButtonText={modalConfig?.cancelButtonText}
      saveData={(e) => {
        handleSync(e);
      }}
      disableSaveButton={
        !hasFolderOrSubfolderSelected ||
        !hasNewSelection ||
        isDisabled ||
        isLoading ||
        componentState.chunk_overlap > Math.floor(componentState.chunk_words / 2)
      }
    >
      <div className="min-h-[300px] bg-gradient-to-br from-gray-50 to-gray-100 py-8 min-h-500px overflow-hidden overflow-y-scroll">
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-md p-2 mb-3">
              <Info size={16} className="text-yellow-500 flex-shrink-0 mt-0.5" />
              {note}
            </div>
            <FolderTree
              disabled={isDisabled}
              loading={isLoading}
              folderId={folderId}
              service={service}
              data={folderTreeData}
              selectedFolders={alreadySelectedList}
              showCheckboxes={true as boolean}
              onSelectionChange={handleSelectionChange}
            />
            {hasNewSelection &&
              chunkInput?.map((i) => (
                <div className="mt-2" key={i.id}>
                  <div className="flex items-center gap-1 mb-[6px]">
                    <span className="text-sm text-[#676767] font-bold">{i.label}</span>
                    <StyledTooltip title={i.infoIconText} placement="right">
                      <span title="Information" aria-label="Information">
                        <Info size={14} className="text-[#676767]" />
                      </span>
                    </StyledTooltip>
                  </div>
                  <div className="radio-btns rounded-md">
                    <div className="flex items-center">
                      <span className="text-sm w-1/3">{i.labelHelper}</span>
                      <input
                        key={i.id}
                        type={i.type}
                        className={i.className}
                        placeholder={i.placeholder}
                        disabled={isDisabled}
                        min={i.min}
                        {...(i.id === 'chunk_overlap'
                          ? { max: Math.floor(componentState.chunk_words / 2) }
                          : {})}
                        value={
                          i.id === 'chunk_words'
                            ? componentState.chunk_words || ''
                            : componentState.chunk_overlap || ''
                        }
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          onChangeHandler(i.id, Number(val));
                        }}
                        onKeyDown={(e) => {
                          if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
                        }}
                      />
                    </div>
                    {componentState.chunk_overlap > Math.floor(componentState.chunk_words / 2) && (
                      <span className="text-red-600">{i.error}</span>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </ActionModal>
  );
}

export default FolderHierarchy;
